package com.eladfinish.bible.contest.screens.browse.contentOfChapter.ui

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.tween
import androidx.compose.foundation.ScrollState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshotFlow
import androidx.compose.ui.text.TextLayoutResult
import androidx.compose.ui.text.TextRange
import com.eladfinish.bible.contest.constants.StringConstants.COLON_SPLITTER
import com.eladfinish.bible.contest.constants.strings.RISHON_ALIYAH_LABEL
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeoutOrNull
import kotlin.math.roundToInt

@Stable
class GoToMarkerController internal constructor(
    private val scope: CoroutineScope,
) {
    internal var scrollState: ScrollState? = null
    internal var layoutResult: TextLayoutResult? = null
    internal var rawText: String = ""
    internal var transientHighlightRange by mutableStateOf<TextRange?>(null)
    private var pendingY: Int? = null
    private var pendingVerse: Int? = null
    private var pendingTextOffset: Int? = null

    /** Set by the page: the text starts this far into the scrolling area. */
    internal var textPaddingPx: Float = 0f

    // The text and the scroll state of the page on screen, as of its last layout
    internal var activeLayout: TextLayoutResult? by mutableStateOf(null)
        private set
    private var activeLayoutScrollState: ScrollState? = null

    fun goToAliyah(which: String) {
        val s = scrollState ?: return

        // "ראשון" has no in-text marker — it is the start of the reading, so scroll to the top
        // (no transient highlight: there is no marker text to highlight)
        if (which == RISHON_ALIYAH_LABEL) {
            transientHighlightRange = null
            scope.launch { s.animateScrollTo(0) }
            return
        }

        val lr = layoutResult ?: return

        val text = lr.layoutInput.text.text
        if (text.isEmpty()) return

        val needle = "[$which]"
        val indexOfNeedle = text.indexOf(needle)
        if (indexOfNeedle == -1) {
            transientHighlightRange = null
            return
        }

        val safeOffset = indexOfNeedle.coerceIn(0, text.length - 1)

        val box = runCatching { lr.getBoundingBox(safeOffset) }.getOrNull() ?: return

        scope.launch {
            val target = box.top.roundToInt().coerceIn(0, s.maxValue)
            s.animateScrollTo(target)

            transientHighlightRange = TextRange(
                start = indexOfNeedle,
                end = (indexOfNeedle + needle.length).coerceAtMost(text.length),
            )
        }
    }

    /**
     * Scrolls to [verse] (1-based) of the chapter on screen and highlights it for a moment, like
     * [goToAliyah] does for an aliyah. When the chapter has not been laid out yet, it waits for it.
     */
    fun goToVerse(verse: Int) {
        pendingVerse = verse
        val layout = activeLayout
        val state = activeLayoutScrollState
        if (layout != null && state != null) goToPendingVerse(layout, state)
    }

    /** Scrolls to the line holding [offset] of the text, once the chapter is laid out at its current width. */
    fun scrollToTextOffset(offset: Int) {
        pendingTextOffset = offset
        val layout = activeLayout
        val state = activeLayoutScrollState
        if (layout != null && state != null) goToPendingTextOffset(layout, state)
    }

    private fun goToPendingTextOffset(layout: TextLayoutResult, state: ScrollState) {
        val offset = pendingTextOffset ?: return
        val length = layout.layoutInput.text.length
        if (length == 0) return
        pendingTextOffset = null

        val top = layout.getLineTop(layout.getLineForOffset(offset.coerceIn(0, length))) + textPaddingPx
        scope.launch {
            withTimeoutOrNull(SCROLL_RANGE_WAIT_MILLIS) { snapshotFlow { state.maxValue }.first { it > 0 } }
            state.scrollTo(top.roundToInt().coerceIn(0, state.maxValue))
        }
    }

    /** The page on screen has been laid out: the verse that was asked for before that can be found now. */
    internal fun onActivePageLaidOut(layout: TextLayoutResult, state: ScrollState) {
        activeLayout = layout
        activeLayoutScrollState = state
        if (pendingVerse != null) goToPendingVerse(layout, state)
        if (pendingTextOffset != null) goToPendingTextOffset(layout, state)
    }

    private fun goToPendingVerse(layout: TextLayoutResult, state: ScrollState) {
        val verse = pendingVerse ?: return
        val text = layout.layoutInput.text.text
        // A chapter that has not been loaded yet has no text: wait for the next layout
        if (text.isEmpty()) return
        pendingVerse = null

        val range = verseRange(text = text, verse = verse) ?: return
        val top = runCatching { layout.getBoundingBox(range.start).top }.getOrNull() ?: return

        scope.launch {
            // The scroll range is known once the layout is measured; a chapter that fits the screen has none
            withTimeoutOrNull(SCROLL_RANGE_WAIT_MILLIS) { snapshotFlow { state.maxValue }.first { it > 0 } }
            state.animateScrollTo(top.roundToInt().coerceIn(0, state.maxValue))
            transientHighlightRange = range
        }
    }

    fun onActiveScrollStateAvailable(state: ScrollState) {
        scrollState = state
        pendingY?.let {
            pendingY = null
            scrollToY(it)
        }
    }

    fun scrollToY(yOffset: Int, animated: Boolean = false) {
        val s = scrollState
        if (s == null) {
            pendingY = yOffset
            return
        }
        scope.launch {
            val target = yOffset.coerceIn(0, s.maxValue)
            if (animated) s.animateScrollTo(target) else s.scrollTo(target)
        }
    }
}

/**
 * The range of verse number [verse] (1-based) in the text of a chapter, from its number up to and including
 * its sof pasuk; `null` when the chapter has fewer verses. The verses of the text are told apart by their sof
 * pasuk, as everywhere else in the app.
 */
internal fun verseRange(text: String, verse: Int): TextRange? {
    if (verse < 1) return null
    var start = 0
    repeat(verse - 1) {
        val colon = text.indexOf(COLON_SPLITTER, startIndex = start)
        if (colon < 0) return null
        start = colon + COLON_SPLITTER.length
    }
    val colon = text.indexOf(COLON_SPLITTER, startIndex = start)
    val end = if (colon < 0) text.length else colon + COLON_SPLITTER.length
    while (start < end && text[start].isWhitespace()) start++
    return if (start < end) TextRange(start, end) else null
}

private const val SCROLL_RANGE_WAIT_MILLIS = 500L

@Composable
internal fun rememberGoToMarkerController(): GoToMarkerController {
    val scope = rememberCoroutineScope()
    return remember { GoToMarkerController(scope) }
}

@Composable
internal fun rememberHighlightAlpha(range: TextRange?): Float {
    val alpha = remember { Animatable(0f) }

    LaunchedEffect(range) {
        if (range == null) return@LaunchedEffect
        alpha.snapTo(1f) // start fully visible
        alpha.animateTo(
            targetValue = 0f, // fade out
            animationSpec = tween(durationMillis = 1200)
        )
    }

    return alpha.value
}
